import { pickLocalizedText } from '@utils/i18n/uiLocale'
import {
  buildRelaxArmVariantCatalog,
  getRelaxBaseCode,
  parseRelaxArmVariantCatalog,
  type RelaxArmPosition,
} from '@utils/atemporal/relaxArmVariants'

/**
 * Lado físico de una unidad RELAX respecto a su eje X local.
 * - `right` → eje +X → variante de brazo `D` (Derecha).
 * - `left`  → eje -X → variante de brazo `I` (Izquierda).
 */
export type RelaxArmSide = 'left' | 'right'

/** Estado de bloqueo por colisión en cada lateral de la unidad RELAX. */
export type RelaxSideBlockState = {
  /** Hay una unidad colisionada en el lateral derecho (eje +X, posición `D`). */
  rightBlocked: boolean
  /** Hay una unidad colisionada en el lateral izquierdo (eje -X, posición `I`). */
  leftBlocked: boolean
}

/** Motivo por el que un cambio de brazo RELAX no se permite. */
export type RelaxArmBlockReason = 'both-sides' | 'right-occupied' | 'left-occupied'

/** Resultado de resolver el cambio de variante RELAX considerando colisiones. */
export type RelaxArmResolution =
  | { kind: 'apply'; nextCatalogID: string }
  | { kind: 'noop' }
  | { kind: 'blocked'; reason: RelaxArmBlockReason }

/** Posiciones de brazo y el lateral (eje X local) que ocupan. */
const POSITION_SIDES: Readonly<Record<RelaxArmPosition, ReadonlyArray<RelaxArmSide>>> = {
  D: ['right'],
  I: ['left'],
  A: ['left', 'right'],
}

/** ¿La posición de brazo cubre el lateral indicado? (D→derecha, I→izquierda, A→ambos). */
export function relaxPositionCoversSide(
  position: RelaxArmPosition | null,
  side: RelaxArmSide,
): boolean {
  if (!position) return false
  return POSITION_SIDES[position].includes(side)
}

/**
 * Ciclo de posiciones de brazo según los lados libres de la unidad:
 * - Ambos lados libres: sin brazos → un lado (D) → ambos (A) → otro lado (I) → sin brazos.
 * - Solo derecha libre (izquierda bloqueada): sin brazos → D → sin brazos.
 * - Solo izquierda libre (derecha bloqueada): sin brazos → I → sin brazos.
 *
 * De este modo el primer clic siempre coloca un único brazo (nunca ambos) y, si
 * un lateral está colisionado, solo se ofrece el lado libre o «sin brazos».
 */
const getArmCycle = (blocked: RelaxSideBlockState): ReadonlyArray<RelaxArmPosition | null> => {
  const rightFree = !blocked.rightBlocked
  const leftFree = !blocked.leftBlocked
  if (rightFree && leftFree) return [null, 'D', 'A', 'I']
  if (rightFree) return [null, 'D']
  if (leftFree) return [null, 'I']
  return [null]
}

const firstArmedPosition = (
  cycle: ReadonlyArray<RelaxArmPosition | null>,
): RelaxArmPosition | null =>
  cycle.find((position): position is RelaxArmPosition => position !== null) ?? null

/** Avanza una posición dentro del ciclo dado; si la actual no existe, vuelve a «sin brazos». */
const stepInCycle = (
  cycle: ReadonlyArray<RelaxArmPosition | null>,
  from: RelaxArmPosition | null,
): RelaxArmPosition | null => {
  const startIndex = cycle.indexOf(from)
  if (startIndex < 0) return null
  return cycle[(startIndex + 1) % cycle.length]
}

const buildResolution = (
  baseCode: string,
  brNumber: number,
  position: RelaxArmPosition | null,
): RelaxArmResolution => ({
  kind: 'apply',
  nextCatalogID: position ? buildRelaxArmVariantCatalog(baseCode, brNumber, position) : baseCode,
})

/**
 * Resuelve el siguiente catálogo de la unidad RELAX al pulsar un brazo,
 * respetando los laterales bloqueados por colisión.
 *
 * Reglas:
 * - Ambos lados colisionados → `blocked` (no se permite ningún cambio de brazo).
 * - Un lado colisionado → se omiten las posiciones que ocupan ese lado
 *   (incluida `A`), permitiendo solo el lado libre o «sin brazos».
 */
export function resolveConstrainedRelaxArmVariant(
  currentCatalogID: unknown,
  clickedBrNumber: number,
  blocked: RelaxSideBlockState,
): RelaxArmResolution {
  const baseCode = getRelaxBaseCode(currentCatalogID)
  if (!baseCode) return { kind: 'noop' }

  if (blocked.rightBlocked && blocked.leftBlocked) {
    return { kind: 'blocked', reason: 'both-sides' }
  }

  const cycle = getArmCycle(blocked)
  const currentParts = parseRelaxArmVariantCatalog(currentCatalogID)
  const currentPosition = currentParts?.position ?? null

  // Cambio de tipo de brazo (BR distinto): conservar posición si sigue siendo
  // válida en este ciclo; si no, mover a la primera posición con brazo.
  if (currentParts && currentParts.brNumber !== clickedBrNumber) {
    const keptPosition = cycle.includes(currentPosition)
      ? currentPosition
      : firstArmedPosition(cycle)
    return buildResolution(baseCode, clickedBrNumber, keptPosition)
  }

  // Mismo BR (o sin variante): avanzar al siguiente paso del ciclo.
  const nextPosition = stepInCycle(cycle, currentPosition)
  return buildResolution(baseCode, clickedBrNumber, nextPosition)
}

/** Mensajes de restricción de brazos RELAX (ES/EN según navegador). */
export function getRelaxArmNotice(reason: RelaxArmBlockReason): string {
  const map: Record<RelaxArmBlockReason, { es: string; en: string }> = {
    'both-sides': {
      es: 'Esta unidad tiene unidades colisionadas en ambos lados y no permite colocar brazos.',
      en: 'This unit has colliding units on both sides and does not allow placing arms.',
    },
    'right-occupied': {
      es: 'Esta unidad tiene una unidad colisionada en el lado derecho; solo se puede colocar el brazo izquierdo o ninguno.',
      en: 'This unit has a colliding unit on the right; only the left arm or none can be placed.',
    },
    'left-occupied': {
      es: 'Esta unidad tiene una unidad colisionada en el lado izquierdo; solo se puede colocar el brazo derecho o ninguno.',
      en: 'This unit has a colliding unit on the left; only the right arm or none can be placed.',
    },
  }
  return pickLocalizedText(map[reason])
}

/** @deprecated Usar getRelaxArmNotice(reason) */
export const RELAX_ARM_NOTICE: Readonly<Record<RelaxArmBlockReason, string>> = new Proxy(
  {} as Record<RelaxArmBlockReason, string>,
  { get: (_t, prop: string) => getRelaxArmNotice(prop as RelaxArmBlockReason) },
)
